import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, BookOpen, Search, Bell, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationContext } from "@/contexts/NotificationContext";

interface NavItem {
  icon: React.ElementType;
  labelKey: string;
  path: string;
  authRequired?: boolean;
}

const navItems: NavItem[] = [
  { icon: Home, labelKey: "nav.home", path: "" },
  { icon: BookOpen, labelKey: "nav.catalog", path: "catalog" },
  { icon: Search, labelKey: "common.search", path: "catalog?focus=search" },
  {
    icon: Bell,
    labelKey: "nav.notifications",
    path: "notifications",
    authRequired: true,
  },
  { icon: User, labelKey: "nav.profile", path: "profile", authRequired: true },
];

export default function MobileNav() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { unreadCount } = useNotificationContext();
  const lang = i18n.language || "fr";

  const isActive = (itemPath: string): boolean => {
    const currentPath = location.pathname
      .replace(`/${lang}`, "")
      .replace(/^\//, "");
    if (itemPath === "" && (currentPath === "" || currentPath === "/"))
      return true;
    if (itemPath === "") return false;
    const cleanItemPath = itemPath.split("?")[0];
    return currentPath.startsWith(cleanItemPath);
  };

  const handleNavigation = (item: NavItem) => {
    if (item.authRequired && !isAuthenticated) {
      navigate(`/${lang}/login`);
      return;
    }
    const basePath = item.path.split("?")[0];
    navigate(`/${lang}/${basePath}`);
  };

  // If not authenticated, replace auth-required items with login
  const displayItems: NavItem[] = navItems.map((item) => {
    if (item.authRequired && !isAuthenticated) {
      if (item.labelKey === "nav.profile") {
        return { icon: User, labelKey: "nav.login", path: "login" };
      }
      return item;
    }
    return item;
  });

  // Filter out duplicate login entries and notifications for guests
  const finalItems = displayItems.filter((item, _index) => {
    if (
      !isAuthenticated &&
      item.authRequired &&
      item.labelKey !== "nav.login"
    ) {
      return false;
    }
    return true;
  });

  // Ensure we have at most 5 items
  const shownItems = finalItems.slice(0, 5);

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 md:hidden",
        "border-t border-border bg-background/95 backdrop-blur-md",
        "pb-safe-bottom",
      )}
      role="navigation"
      aria-label={t("accessibility.mobile_navigation")}
    >
      <div className="flex items-center justify-around px-2 py-1">
        {shownItems.map((item) => {
          const active = isActive(item.path);
          const Icon = item.icon;

          return (
            <button
              key={item.labelKey + item.path}
              onClick={() => handleNavigation(item)}
              className={cn(
                "relative flex flex-col items-center justify-center",
                "min-w-[56px] gap-0.5 rounded-lg px-3 py-2",
                "text-xs font-medium transition-colors duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-current={active ? "page" : undefined}
            >
              {/* Active indicator */}
              {active && (
                <motion.div
                  layoutId="mobile-nav-indicator"
                  className="absolute -top-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-primary"
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                />
              )}

              <motion.div
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.1 }}
                className="relative"
              >
                <Icon
                  className={cn(
                    "h-5 w-5 transition-colors duration-200",
                    active ? "text-primary" : "text-muted-foreground",
                  )}
                  strokeWidth={active ? 2.5 : 2}
                />
                {/* Unread notification badge on the Bell icon */}
                {item.labelKey === "nav.notifications" && unreadCount > 0 && (
                  <AnimatePresence>
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 25,
                      }}
                      className={cn(
                        "absolute -right-1.5 -top-1 flex items-center justify-center rounded-full bg-destructive text-[8px] font-bold text-destructive-foreground",
                        unreadCount > 9
                          ? "h-3.5 min-w-[14px] px-0.5"
                          : "h-3.5 w-3.5",
                      )}
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </motion.span>
                  </AnimatePresence>
                )}
              </motion.div>

              <span
                className={cn(
                  "truncate text-[10px] leading-tight",
                  active
                    ? "font-semibold text-primary"
                    : "text-muted-foreground",
                )}
              >
                {t(item.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
